import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Building, CheckCircle2, Plus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

interface CustomerData {
  id: string;
  name: string;
  legal_name?: string;
}

const Step2ChildCustomers = () => {
  const navigate = useNavigate();
  const { state, setSelectedChildCustomers } = useSubscriptionWizard();
  const [availableCustomers, setAvailableCustomers] = useState<CustomerData[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerData[]>([]);

  useEffect(() => {
    if (!state.selectedCustomer || !state.subscriptionType) {
      // Redirect back to step 1 if no customer or subscription type is selected
      navigate('../step1', { relative: 'path' });
      return;
    }

    // This step is only for 'split' subscriptions
    // Simple subscriptions should go directly to step3 (contacts)
    if (state.subscriptionType !== 'split') {
      navigate('../step1c', { relative: 'path' });
      return;
    }

    fetchAvailableCustomers();
    // Initialize with previously selected child customers
    setSelectedCustomers(state.selectedChildCustomers);
  }, [state.selectedCustomer, state.subscriptionType, navigate]);

  // Filter customers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(availableCustomers);
    } else {
      const filtered = availableCustomers.filter(customer => 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.legal_name && customer.legal_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [availableCustomers, searchTerm]);

  const fetchAvailableCustomers = async () => {
    if (!state.selectedCustomer) return;

    try {
      setLoading(true);
      
      // Get all customers except the selected owner customer
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, legal_name')
        .neq('id', state.selectedCustomer.id)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      
      setAvailableCustomers(data || []);
      // Reset search when data is fetched
      setSearchTerm('');
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Falha ao carregar clientes disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerToggle = (customer: CustomerData) => {
    setSelectedCustomers(prev => {
      const isSelected = prev.some(c => c.id === customer.id);
      if (isSelected) {
        return prev.filter(c => c.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  const handleContinue = () => {
    // Convert CustomerData to Customer interface expected by context
    const customers = selectedCustomers.map(c => ({
      id: c.id,
      name: c.name,
      legal_name: c.legal_name || c.name // Provide default if missing
    }));
    setSelectedChildCustomers(customers);
    navigate('../step3', { relative: 'path' });
  };

  const isCustomerSelected = (customerId: string) => {
    return selectedCustomers.some(c => c.id === customerId);
  };

  return (
    <SubscriptionWizardLayout
      currentStep={2}
      totalSteps={5}
      title="Selecionar Clientes Filho"
      description={`Selecione os clientes filho para associar à assinatura de ${state.selectedCustomer?.name || 'o cliente'}. Você pode deixar em branco se não há clientes filho.`}
      onNext={handleContinue}
      onPrevious={() => navigate('../step1c', { relative: 'path' })}
      isNextDisabled={false} // Allow continuing without child customers
    >
      <div className="space-y-6">
        {/* Owner Customer Display */}
        <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
          <div className="text-sm font-extrabold text-blue-800">Cliente Principal (Owner)</div>
          <div className="font-extrabold mt-1 flex items-center text-blue-900">
            <Building className="h-4 w-4 mr-2" />
            {state.selectedCustomer?.name}
          </div>
        </div>

        {/* Selected Child Customers Summary */}
        {selectedCustomers.length > 0 && (
          <div className="bg-muted p-4 rounded-md">
            <div className="text-sm font-extrabold mb-2">Clientes Filho Selecionados ({selectedCustomers.length})</div>
            <div className="flex flex-wrap gap-2">
              {selectedCustomers.map(customer => (
                <Badge key={customer.id} variant="default">
                  {customer.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Available Customers */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-extrabold">Clientes Disponíveis</h3>
              <p className="text-sm text-muted-foreground">
                Clique nos clientes que deseja associar como filhos na assinatura
              </p>
            </div>
          </div>

          {/* Search Input */}
          {availableCustomers.length > 0 && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                type="text"
                placeholder="Buscar clientes por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Carregando clientes...</p>
            </div>
          ) : filteredCustomers.length === 0 && availableCustomers.length > 0 ? (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhum cliente encontrado</p>
              <p className="text-sm text-muted-foreground mt-1">
                Tente ajustar sua busca ou{' '}
                <button 
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  limpar filtros
                </button>
              </p>
            </div>
          ) : availableCustomers.length === 0 ? (
            <div className="text-center py-8">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhum cliente filho disponível</p>
              <p className="text-sm text-muted-foreground mt-1">
                Você pode continuar sem clientes filho ou criar novos clientes primeiro
              </p>
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredCustomers.map((customer) => {
                const isSelected = isCustomerSelected(customer.id);
                return (
                  <Card
                    key={customer.id}
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                      isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => handleCustomerToggle(customer)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Building className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <div className="font-extrabold">{customer.name}</div>
                            {customer.legal_name && customer.legal_name !== customer.name && (
                              <div className="text-sm text-muted-foreground">{customer.legal_name}</div>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Search Results Summary */}
          {searchTerm && filteredCustomers.length > 0 && (
            <div className="text-sm text-muted-foreground text-center">
              Mostrando {filteredCustomers.length} de {availableCustomers.length} clientes
            </div>
          )}
        </div>

        {/* Info about current limitations */}
        {selectedCustomers.length > 1 && (
          <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
            <div className="text-sm text-yellow-800">
              <strong>Nota:</strong> Múltiplos clientes filho foram selecionados. 
              Atualmente, apenas o primeiro cliente será totalmente integrado devido a limitações 
              da estrutura do banco de dados. Os demais serão preparados para futura implementação.
            </div>
          </div>
        )}
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step2ChildCustomers;